// Sync Error Recovery Mechanisms
// Handles recovery from various sync failure scenarios

import { mutationQueue } from '../../queue/mutation-queue.js';
import { syncEngine } from '../engine/sync-engine.js';
import { MutationStatus } from '../types/sync-types.js';
import { db, cacheStore, syncQueueStore } from '../../db/database.js';

class SyncRecoveryManager {
  constructor() {
    this.recoveryAttempts = 0;
    this.maxRecoveryAttempts = 3;
    this.isRecovering = false;
  }

  /**
   * Recover from interrupted sync
   */
  async recoverInterruptedSync() {
    console.log('[SyncRecovery] Recovering from interrupted sync');
    
    const { processing } = await mutationQueue.getStats();

    if (processing > 0) {
      console.log(`[SyncRecovery] Found ${processing} mutations stuck in processing state`);
      
      // Reset processing mutations to pending
      const allKeys = await db.getKeys(syncQueueStore);
      const mutationKeys = allKeys.filter((key) => key.startsWith('mutation:'));
      
      const inFlightGraceMs = 30_000;

      for (const key of mutationKeys) {
        const entry = await db.get(key, syncQueueStore);
        if (entry?.data?.status !== MutationStatus.PROCESSING) continue;

        const lastTouch =
          entry.data.lastAttemptAt || entry.metadata?.updatedAt || entry.data.createdAt || 0;
        if (Date.now() - lastTouch < inFlightGraceMs) {
          continue;
        }

        await mutationQueue.updateStatus(entry.data.mutationId, MutationStatus.PENDING, 0);
        console.log('[SyncRecovery] Reset mutation to pending:', entry.data.mutationId);
      }
    }

    // Restart sync engine
    syncEngine.resume();
  }

  /**
   * Recover from crashed replay
   */
  async recoverCrashedReplay() {
    console.log('[SyncRecovery] Recovering from crashed replay');
    
    const issues = await mutationQueue.validateQueue();

    // Fix queue integrity issues
    for (const issue of issues) {
      if (issue.issue === 'stale') {
        console.log('[SyncRecovery] Removing stale mutation:', issue.key);
        await db.delete(issue.key, syncQueueStore);
      }
      
      if (issue.issue.startsWith('missing_')) {
        console.log('[SyncRecovery] Removing malformed mutation:', issue.key);
        await db.delete(issue.key, syncQueueStore);
      }
    }

    // Reset any stuck mutations
    await this.resetStuckMutations();
  }

  /**
   * Recover from malformed mutations
   */
  async recoverMalformedMutations() {
    console.log('[SyncRecovery] Recovering from malformed mutations');
    
    const allKeys = await db.getKeys(syncQueueStore);
    const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));

    let removed = 0;
    
    for (const key of mutationKeys) {
      const entry = await db.get(key, syncQueueStore);
      
      if (!entry || !entry.data) {
        await db.delete(key, syncQueueStore);
        removed++;
        continue;
      }

      const mutation = entry.data;
      
      // Check required fields
      const required = ['mutationId', 'entityType', 'entityId', 'operationType', 'payload', 'idempotencyKey'];
      const missing = required.filter(field => !mutation[field]);
      
      if (missing.length > 0) {
        console.log('[SyncRecovery] Removing malformed mutation:', key, 'missing:', missing);
        await db.delete(key, syncQueueStore);
        removed++;
      }
    }

    console.log(`[SyncRecovery] Removed ${removed} malformed mutations`);
    return removed;
  }

  /**
   * Recover from stale locks
   */
  async recoverStaleLocks() {
    console.log('[SyncRecovery] Recovering from stale locks');
    
    // Reset any mutations that have been in processing state too long
    const allKeys = await db.getKeys(syncQueueStore);
    const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));

    const staleThreshold = 5 * 60 * 1000; // 5 minutes
    let recovered = 0;
    
    for (const key of mutationKeys) {
      const entry = await db.get(key, syncQueueStore);
      
      if (entry?.data?.status === MutationStatus.PROCESSING) {
        const lastAttempt = entry.data.lastAttemptAt || entry.data.createdAt || 0;
        const age = Date.now() - lastAttempt;
        
        if (age > staleThreshold) {
          console.log('[SyncRecovery] Recovering stale lock:', key);
          await mutationQueue.updateStatus(entry.data.mutationId, MutationStatus.PENDING, 0);
          recovered++;
        }
      }
    }

    console.log(`[SyncRecovery] Recovered ${recovered} stale locks`);
    return recovered;
  }

  /**
   * Recover from queue corruption
   */
  async recoverQueueCorruption() {
    console.log('[SyncRecovery] Recovering from queue corruption');
    
    // Validate queue
    const issues = await mutationQueue.validateQueue();
    
    if (issues.length === 0) {
      console.log('[SyncRecovery] No queue corruption detected');
      return 0;
    }

    console.log(`[SyncRecovery] Found ${issues.length} queue issues`);
    
    // Remove corrupted entries
    for (const issue of issues) {
      await db.delete(issue.key, syncQueueStore);
    }

    return issues.length;
  }

  /**
   * Recover from invalid entities
   */
  async recoverInvalidEntities() {
    console.log('[SyncRecovery] Recovering from invalid entities');
    
    const allKeys = await db.keys(cacheStore);
    const entityKeys = allKeys.filter(key => 
      !key.startsWith('mutation:') && 
      !key.startsWith('metadata:') &&
      !key.startsWith('image:') &&
      !key.startsWith('query:')
    );
    
    let recovered = 0;
    
    for (const key of entityKeys) {
      const entity = await db.get(key, cacheStore);
      
      if (!entity) {
        // Remove empty entries
        await db.delete(key, cacheStore);
        recovered++;
        continue;
      }

      // Check for required fields based on entity type
      const entityType = key.split(':')[0];
      if (!this.validateEntityStructure(entity, entityType)) {
        console.log('[SyncRecovery] Removing invalid entity:', key);
        await db.delete(key, cacheStore);
        recovered++;
      }
    }

    console.log(`[SyncRecovery] Recovered ${recovered} invalid entities`);
    return recovered;
  }

  /**
   * Validate entity structure
   */
  validateEntityStructure(entity) {
    // Basic validation - entity should have an ID
    if (!entity.id) {
      return false;
    }

    // Type-specific validation could be added here
    return true;
  }

  /**
   * Recover from server mismatch
   */
  async recoverServerMismatch() {
    console.log('[SyncRecovery] Recovering from server mismatch');
    
    // This would involve fetching fresh data from server
    // For now, we'll just reset failed mutations for retry
    const retried = await mutationQueue.retryFailed();
    
    console.log(`[SyncRecovery] Retried ${retried} failed mutations`);
    return retried;
  }

  /**
   * Reset stuck mutations
   */
  async resetStuckMutations() {
    console.log('[SyncRecovery] Resetting stuck mutations');
    
    const allKeys = await db.getKeys(syncQueueStore);
    const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));

    let reset = 0;
    
    for (const key of mutationKeys) {
      const entry = await db.get(key, syncQueueStore);
      
      if (entry?.data?.status === MutationStatus.PROCESSING) {
        await mutationQueue.updateStatus(entry.data.mutationId, MutationStatus.PENDING, 0);
        reset++;
      }
    }

    console.log(`[SyncRecovery] Reset ${reset} stuck mutations`);
    return reset;
  }

  /**
   * Full recovery process
   */
  async fullRecovery() {
    if (this.isRecovering) {
      console.log('[SyncRecovery] Recovery already in progress');
      return;
    }

    this.isRecovering = true;
    this.recoveryAttempts++;

    console.log(`[SyncRecovery] Starting full recovery (attempt ${this.recoveryAttempts})`);

    try {
      // Step 1: Recover from interrupted sync
      await this.recoverInterruptedSync();
      
      // Step 2: Recover from crashed replay
      await this.recoverCrashedReplay();
      
      // Step 3: Recover malformed mutations
      await this.recoverMalformedMutations();
      
      // Step 4: Recover stale locks
      await this.recoverStaleLocks();
      
      // Step 5: Recover queue corruption
      await this.recoverQueueCorruption();
      
      // Step 6: Recover invalid entities
      await this.recoverInvalidEntities();
      
      // Step 7: Recover from server mismatch
      await this.recoverServerMismatch();

      console.log('[SyncRecovery] Full recovery complete');
      this.recoveryAttempts = 0;
      
      return { success: true };
    } catch (error) {
      console.error('[SyncRecovery] Recovery failed:', error);
      
      if (this.recoveryAttempts >= this.maxRecoveryAttempts) {
        console.error('[SyncRecovery] Max recovery attempts reached');
        return { success: false, error: 'Max recovery attempts reached' };
      }

      return { success: false, error };
    } finally {
      this.isRecovering = false;
    }
  }

  /**
   * Get recovery status
   */
  getRecoveryStatus() {
    return {
      isRecovering: this.isRecovering,
      recoveryAttempts: this.recoveryAttempts,
      maxRecoveryAttempts: this.maxRecoveryAttempts,
    };
  }

  /**
   * Reset recovery state
   */
  resetRecoveryState() {
    this.recoveryAttempts = 0;
    this.isRecovering = false;
  }
}

// Export singleton instance
export const syncRecoveryManager = new SyncRecoveryManager();
